import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, CreditCard, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const LOGO_URL = "/holapay-mark.svg";

// Translations for public invoice page
const translations = {
  it: {
    invoiceNotFound: 'Fattura non trovata',
    paymentCompleted: 'Pagamento completato!',
    paymentFailed: 'Pagamento fallito',
    paymentConfirmError: 'Errore nella conferma del pagamento',
    paymentCreateError: 'Errore nella creazione del pagamento',
    dueDate: 'Scadenza',
    description: 'Descrizione',
    qty: 'Qtà',
    price: 'Prezzo',
    subtotal: 'Subtotale',
    vat: 'IVA',
    total: 'Totale',
    pay: 'Paga',
    securePayment: 'Pagamento sicuro con',
    paidBadge: 'Pagata',
    pendingBadge: 'In attesa',
    overdueBadge: 'In ritardo',
    expiredBadge: 'Link scaduto',
    cancelledBadge: 'Annullata',
    refundedBadge: 'Rimborsata',
    notPayable: 'Questa fattura non può essere pagata online.',
    paymentSuccess: 'Pagamento completato con successo!',
    paymentFailedMsg: 'Il pagamento non è andato a buon fine',
    invoiceNumber: 'Fattura',
    vatNumber: 'P.IVA',
    processing: 'Elaborazione...'
  },
  en: {
    invoiceNotFound: 'Invoice not found',
    paymentCompleted: 'Payment completed!',
    paymentFailed: 'Payment failed',
    paymentConfirmError: 'Error confirming payment',
    paymentCreateError: 'Error creating payment',
    dueDate: 'Due Date',
    description: 'Description',
    qty: 'Qty',
    price: 'Price',
    subtotal: 'Subtotal',
    vat: 'VAT',
    total: 'Total',
    pay: 'Pay',
    securePayment: 'Secure payment with',
    paidBadge: 'Paid',
    pendingBadge: 'Pending',
    overdueBadge: 'Overdue',
    expiredBadge: 'Link expired',
    cancelledBadge: 'Cancelled',
    refundedBadge: 'Refunded',
    notPayable: 'This invoice cannot be paid online.',
    paymentSuccess: 'Payment completed successfully!',
    paymentFailedMsg: 'Payment was not successful',
    invoiceNumber: 'Invoice',
    vatNumber: 'VAT No.',
    processing: 'Processing...'
  }
};

export default function PublicInvoicePage() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Get language from merchant (default to 'en')
  const lang = data?.merchant?.language || 'en';
  const t = translations[lang] || translations.en;

  const fetchInvoice = useCallback(async (signal) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/public/invoice/${encodeURIComponent(invoiceId)}`,
        { signal }
      );
      setData(response.data);
      return response.data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        setData(null);
        toast.error(translations.en.invoiceNotFound);
      }
      return null;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [invoiceId]);

  const capturePayment = useCallback(async (orderId, signal, language = 'en') => {
    const messages = translations[language] || translations.en;
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/capture-order/${encodeURIComponent(orderId)}`,
        null,
        { params: { invoice_id: invoiceId }, signal }
      );
      if (response.data.status === 'success') {
        setPaymentStatus('success');
        toast.success(messages.paymentCompleted);
        fetchInvoice(signal);
      } else {
        setPaymentStatus('failed');
        toast.error(messages.paymentFailed);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        setPaymentStatus('failed');
        toast.error(messages.paymentConfirmError);
      }
    } finally {
      if (!signal?.aborted) setProcessing(false);
    }
  }, [fetchInvoice, invoiceId]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    const orderId = new URLSearchParams(window.location.search).get('token');
    const initialize = async () => {
      const invoiceData = await fetchInvoice(controller.signal);
      if (
        orderId
        && invoiceData?.link_state === 'valid'
        && invoiceData.merchant?.gateway === 'paypal'
      ) {
        await capturePayment(
          orderId,
          controller.signal,
          invoiceData.merchant?.language || 'en'
        );
      }
    };
    initialize();

    return () => controller.abort();
  }, [capturePayment, fetchInvoice, invoiceId]);

  const handlePayment = async (selectedGateway = null, selectedGatewayId = null) => {
    setProcessing(true);
    try {
      // Use selected gateway or default from available_gateways
      const availableGateways = data.available_gateways || [];
      const primaryGw = availableGateways.find(g => g.is_primary) || availableGateways[0];
      
      const gateway = selectedGateway || primaryGw?.name || data.merchant?.gateway;
      const gatewayId = selectedGatewayId || primaryGw?.id || data.merchant?.gateway_id || null;
      if (!gateway) {
        toast.error(t.paymentCreateError);
        setProcessing(false);
        return;
      }
      
      // Select the correct API endpoint based on gateway
      let apiEndpoint = `${API_URL}/api/payments/create-order`;
      if (gateway === 'viva_wallet') {
        apiEndpoint = `${API_URL}/api/payments/viva/create-order`;
      } else if (gateway === 'roxpay') {
        apiEndpoint = `${API_URL}/api/payments/roxpay/create-order`;
      } else if (gateway === 'crypto') {
        apiEndpoint = `${API_URL}/api/payments/crypto/create-order`;
      } else if (gateway === 'nexi') {
        apiEndpoint = `${API_URL}/api/payments/nexi/create-order`;
      } else if (gateway === 'payplug') {
        apiEndpoint = `${API_URL}/api/payments/payplug/create-order`;
      } else if (gateway === 'sumup') {
        apiEndpoint = `${API_URL}/api/payments/sumup/create-order`;
      } else if (gateway === 'mollie') {
        apiEndpoint = `${API_URL}/api/payments/mollie/create-order`;
      } else if (gateway === 'silkpay') {
        apiEndpoint = `${API_URL}/api/payments/silkpay/create-order`;
      }
      
      // Build params - include gateway_id for proper gateway selection
      const params = { invoice_id: invoiceId };
      
      // CRITICAL: Pass gateway_id to backend
      if (gatewayId) {
        params.selected_gateway_id = gatewayId;
      }
      
      const response = await axios.post(apiEndpoint, null, { params });

      // Handle different gateway responses
      if (response.data.approval_url) {
        // PayPal
        window.location.href = response.data.approval_url;
      } else if (response.data.hosted_checkout_url) {
        // SumUp Hosted Checkout
        window.location.href = response.data.hosted_checkout_url;
      } else if (response.data.checkout_url) {
        // Viva Wallet / Rox Pay / others
        window.location.href = response.data.checkout_url;
      } else if (response.data.invoice_url) {
        // Crypto (NOWPayments)
        window.location.href = response.data.invoice_url;
      } else {
        toast.error(t.paymentCreateError);
        setProcessing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t.paymentCreateError);
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const locale = lang === 'en' ? 'en-US' : 'it-IT';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount || 0);
    } catch {
      return `${currency || 'EUR'} ${amount || 0}`;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-emerald-100 text-emerald-700',
      expired: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700',
      refunded: 'bg-violet-100 text-violet-700',
      overdue: 'bg-amber-100 text-amber-700'
    };
    const labelsIt = {
      draft: 'Bozza',
      sent: 'Da pagare',
      paid: 'Pagata',
      expired: t.expiredBadge,
      cancelled: t.cancelledBadge,
      refunded: t.refundedBadge,
      overdue: t.overdueBadge
    };
    const labelsEn = {
      draft: 'Draft',
      sent: 'To pay',
      paid: 'Paid',
      expired: t.expiredBadge,
      cancelled: t.cancelledBadge,
      refunded: t.refundedBadge,
      overdue: t.overdueBadge
    };
    const labels = lang === 'en' ? labelsEn : labelsIt;
    return <Badge className={styles[status] || styles.cancelled}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h1 className="text-xl font-heading font-bold text-slate-900 mb-2">
              {translations.en.invoiceNotFound}
            </h1>
            <p className="text-slate-600">
              {lang === 'en' ? 'The requested invoice does not exist.' : 'La fattura richiesta non esiste.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { invoice, merchant } = data;
  const branding = merchant?.branding || {};
  const primaryColor = branding.primary_color || '#7c3aed';
  const buttonColor = branding.button_color || '#7c3aed';

  const dueDate = invoice.due_date
    ? new Date(`${String(invoice.due_date).slice(0, 10)}T00:00:00`)
    : null;
  const overdue = invoice.status === 'sent'
    && dueDate
    && !Number.isNaN(dueDate.getTime())
    && dueDate.getTime() < new Date().setHours(0, 0, 0, 0);
  const canPay = invoice.status === 'sent' && data.link_state === 'valid';
  const statusForBadge = overdue ? 'overdue' : invoice.status;

  return (
    <div className="hola-public-page hola-invoice-page min-h-screen bg-slate-50 py-8 px-4" data-testid="public-invoice-page">
      <Card className="max-w-2xl mx-auto">
        {/* Header */}
        <div 
          className="p-6 border-b flex items-center justify-between"
          style={{ backgroundColor: primaryColor + '10' }}
        >
          <div className="flex items-center gap-4">
            <img 
              src={branding.logo_url || LOGO_URL} 
              alt="Logo"
              className="w-12 h-12 rounded-xl object-cover"
              onError={(e) => { e.target.src = LOGO_URL; }}
            />
            <div>
              <h1 className="font-heading font-bold text-lg" style={{ color: primaryColor }}>
                {branding.display_name || merchant?.business_name || 'Fattura'}
              </h1>
              {branding.legal_name && (
                <p className="text-sm text-slate-500">{branding.legal_name}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading font-bold text-lg">{invoice.invoice_number}</p>
            {getStatusBadge(statusForBadge)}
          </div>
        </div>

        <CardContent className="p-6">
          {/* Invoice Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* From */}
            <div>
              <p className="text-sm text-slate-500 mb-2">{lang === 'en' ? 'From' : 'Da'}</p>
              <p className="font-medium">{branding.display_name || merchant?.business_name}</p>
              {branding.address && <p className="text-sm text-slate-600">{branding.address}</p>}
              {branding.vat_number && <p className="text-sm text-slate-600">{t.vatNumber}: {branding.vat_number}</p>}
              {branding.email && <p className="text-sm text-slate-600">{branding.email}</p>}
            </div>
            {/* To */}
            <div>
              <p className="text-sm text-slate-500 mb-2">{lang === 'en' ? 'To' : 'A'}</p>
              <p className="font-medium">{invoice.customer_name}</p>
              <p className="text-sm text-slate-600">{invoice.customer_email}</p>
              {invoice.customer_address && <p className="text-sm text-slate-600">{invoice.customer_address}</p>}
              {invoice.customer_vat && <p className="text-sm text-slate-600">{t.vatNumber}: {invoice.customer_vat}</p>}
            </div>
          </div>

          {/* Due Date */}
          <div className="mb-6 p-4 rounded-lg bg-slate-50 flex items-center justify-between">
            <span className="text-slate-600">{t.dueDate}</span>
             <span className="font-medium">
               {dueDate && !Number.isNaN(dueDate.getTime())
                 ? dueDate.toLocaleDateString(lang === 'en' ? 'en-US' : 'it-IT')
                 : '—'}
             </span>
          </div>

          {/* Items */}
          <div className="border rounded-lg mb-6 overflow-hidden">
            <Table>
              <TableBody>
                <TableRow className="bg-slate-50">
                  <TableCell className="font-medium">{t.description}</TableCell>
                  <TableCell className="text-right font-medium">{t.qty}</TableCell>
                  <TableCell className="text-right font-medium">{t.price}</TableCell>
                  <TableCell className="text-right font-medium">{t.total}</TableCell>
                </TableRow>
                {(invoice.items || []).map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.unit_price, invoice.currency)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.total, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="space-y-2 mb-8">
            <div className="flex justify-between text-slate-600">
              <span>{t.subtotal}</span>
              <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>{t.vat}</span>
              <span>{formatCurrency(invoice.tax_total, invoice.currency)}</span>
            </div>
            {invoice.discount_total > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>{lang === 'en' ? 'Discount' : 'Sconto'}</span>
                <span>-{formatCurrency(invoice.discount_total, invoice.currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-2 border-t">
              <span>{t.total}</span>
              <span style={{ color: primaryColor }}>{formatCurrency(invoice.total, invoice.currency)}</span>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mb-6 p-4 rounded-lg bg-slate-50">
              <p className="text-sm text-slate-500 mb-1">{lang === 'en' ? 'Notes' : 'Note'}</p>
              <p className="text-slate-700">{invoice.notes}</p>
            </div>
          )}

          {/* Payment Button */}
          {canPay ? (
            <>
              {paymentStatus === 'success' ? (
                <div className="text-center p-6 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                  <p className="font-heading font-bold text-lg text-emerald-700">{t.paymentSuccess}</p>
                </div>
              ) : paymentStatus === 'failed' ? (
                <div className="text-center p-6 bg-red-50 rounded-lg">
                  <XCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
                  <p className="font-heading font-bold text-lg text-red-700 mb-3">{t.paymentFailedMsg}</p>
                  <Button onClick={() => setPaymentStatus(null)} variant="outline">
                    {lang === 'en' ? 'Retry' : 'Riprova'}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handlePayment}
                  disabled={processing || !data.available_gateways?.length}
                  className="w-full h-12 text-lg font-medium text-white"
                  style={{ backgroundColor: buttonColor }}
                  data-testid="pay-invoice-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      {t.pay} {formatCurrency(invoice.total, invoice.currency)}
                    </>
                  )}
                </Button>
              )}
              
              {data.available_gateways?.length > 0 ? (
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-500">
                  <Lock className="w-4 h-4" />
                  {t.securePayment} {data.available_gateways[0]?.display_name || data.merchant?.gateway_display}
                </div>
              ) : (
                <p className="mt-3 text-center text-sm text-amber-700" role="status">
                  {t.notPayable}
                </p>
              )}
            </>
          ) : invoice.status !== 'paid' ? (
            <div className="text-center p-5 rounded-lg bg-slate-50 text-slate-600" data-testid="invoice-not-payable">
              {t.notPayable}
            </div>
          ) : null}

          {invoice.status === 'paid' && (
            <div className="text-center p-6 bg-emerald-50 rounded-lg">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <p className="font-heading font-bold text-lg text-emerald-700">{lang === 'en' ? 'Invoice Paid' : 'Fattura Pagata'}</p>
              <p className="text-sm text-emerald-600 mt-1">
                {lang === 'en' ? 'Paid on' : 'Pagata il'} {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'it-IT') : '-'}
              </p>
            </div>
          )}

          {/* Footer */}
          {branding.footer_text && (
            <p className="text-xs text-slate-400 text-center mt-6">
              {branding.footer_text}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
